// File: src/pages/AllProductPage.tsx

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShoppingCart } from 'lucide-react';

interface Product {
  id: number;
  productName: string;
  description: string;
  price: number;
  discount?: number;
  imageUrl?: string;
  status: string;
}

const API_BASE_URL = 'https://ayushvatika.com';
const IMAGE_BASE_URL = 'https://ayushvatika.com/images/products/';

const AllProductPage: React.FC = () => {
  const { parentSlug, childSlug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // SEO Title
  const pageTitle = childSlug 
    ? `Shop - Ayush Vatika` 
    : parentSlug
    ? `Shop - Ayush Vatika`
    : 'All Ayurvedic Products - Ayush Vatika';

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        let url = `${API_BASE_URL}/api/products`;
        
        if (parentSlug && childSlug) {
           url = `${API_BASE_URL}/api/products/category/id/${childSlug}`;
        } else if (parentSlug) {
           url = `${API_BASE_URL}/api/products/category/id/${parentSlug}`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setProducts(data);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [parentSlug, childSlug]);

  const calculateFinalPrice = (price: number, discount?: number): number => {
    return (discount && discount > 0) ? price - (price * discount / 100) : price;
  };

  if (error) return <div className="text-center mt-20 text-red-500">Error: {error}</div>;

  return (
    <div className="bg-gray-50 min-h-screen font-sans">
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        
        {/* --- PAGE HEADER --- */}
        <div className="text-center mb-10">
            {/* <h1 className="text-3xl md:text-4xl font-bold text-gray-800 capitalize mb-2">
                {childSlug || parentSlug || 'Our Collection'}
            </h1> */}
            <p className="text-gray-500">Discover nature's best remedies & herbal care</p>
        </div>

        {/* --- PRODUCT GRID --- */}
        {loading ? (
            // Loading Skeleton
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="h-80 bg-gray-200 rounded-lg animate-pulse"></div>
            ))}
            </div>
        ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
            <p className="text-gray-500 text-lg">No products found here.</p>
            {/* <button onClick={() => navigate('/product')} className="mt-4 text-blue-600 font-medium hover:underline">View All Products</button> */}
            </div>
        ) : (
            // Changed xl:grid-cols-5 to xl:grid-cols-4 to strictly keep max 4 items per row
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 md:gap-6">
            {products.map((product) => (
                <Link key={product.id} to={`/product/${product.id}`} className="group block h-full">
                
                <div className="bg-white rounded-lg border border-gray-100 hover:border-green-300 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col overflow-hidden relative">
                    
                    {/* --- DEMO RIBBON (Corner) --- */}
                    <div className="absolute top-[12px] -left-[28px] w-[100px] bg-red-600 text-white text-[10px] font-bold py-1 text-center -rotate-45 shadow-sm z-20 tracking-wider">
                      DEMO
                    </div>

                    {/* Badge */}
                    {product.status === 'out of stock' ? (
                      <span className="absolute top-2 right-2 bg-gray-500 text-white text-[10px] font-bold px-2 py-0.5 rounded z-10">SOLD OUT</span>
                    ) : product.discount && product.discount > 0 ? (
                      <span className="absolute top-2 right-2 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded z-10">-{product.discount}%</span>
                    ) : null}

                    {/* --- IMAGE CONTAINER --- */}
                    <div className="h-40 md:h-56 w-full bg-white p-4 flex items-center justify-center relative border-b border-gray-50">
                    {product.imageUrl ? (
                        <img
                        src={`${IMAGE_BASE_URL}${product.imageUrl}`}
                        alt={product.productName}
                        className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        />
                    ) : (
                        <div className="text-gray-300 text-xs">No Image</div>
                    )}
                    </div>

                    {/* --- CONTENT SECTION --- */}
                    <div className="p-3 flex flex-col flex-grow">
                        
                        {/* Title */}
                        <h2 className="text-sm md:text-base font-bold text-gray-800 line-clamp-1 mb-1 group-hover:text-green-700 transition-colors">
                            {product.productName}
                        </h2>

                        
                        <p className="text-xs text-gray-500 line-clamp-2 mb-3 h-8 leading-tight overflow-hidden text-ellipsis">
                            {product.description || "Ayurvedic herbal product for natural wellness and health."}
                        </p>
                        
                        {/* Price & Button Area */}
                        <div className="mt-auto">
                            <div className="flex items-center flex-wrap gap-2 mb-3">
                                <span className="text-base md:text-lg font-bold text-gray-900">
                                    ₹{calculateFinalPrice(product.price, product.discount).toFixed(0)}
                                </span>
                                {(product.discount && product.discount > 0) && (
                                    <>
                                        <span className="text-xs text-gray-400 line-through">
                                        ₹{product.price.toFixed(0)}
                                        </span>
                                        <span className="text-xs text-green-600 font-bold hidden md:inline">
                                            {product.discount}% off
                                        </span>
                                    </>
                                )}
                            </div>

                            <button
                                className={`w-full flex items-center justify-center gap-2 py-2 rounded text-xs md:text-sm font-bold transition-colors uppercase tracking-wide
                                ${product.status === 'out of stock' 
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-100'}`}
                                disabled={product.status === 'out of stock'}
                            >
                                <ShoppingCart size={16} />
                                {product.status === 'out of stock' ? 'No Stock' : 'Add to Cart'}
                            </button>
                        </div>
                    </div>
                </div>
                </Link>
            ))}
            </div>
        )}
      </div>
    </div>
  );
};

export default AllProductPage;